import { useEffect, useState, type FunctionComponent } from "react";
import {
  useParams,
  NavLink,
  Outlet,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import Spinner from "../../../components/spinner.components";
import type { ICampaign } from "../../../types/models/campaign.models.types";
import { useToast } from "../../../hooks/use-toast";
import { handleHttpError } from "../../../utils/utils";
import { useGetCampaignByIdEphemeralHttp } from "../../../hooks/http/ephemeral/campaigns/get-campaign-by-id.ephemeral.http";
import useSearchOpenStateStore from "../../../store/search-open-state.store";
import useHistoryPathStore from "../../../store/history-path.store";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import { Input } from "../../../components/ui/input";
import { XIcon } from "lucide-react";
import MemedLogo from "../../../assets/svgs/common/solo-logo.svg";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../../../components/ui/sheet";
import { DashboardSidebar } from "../../../components/dashboard-sidebar.components";
import HamburgerIcon from "../../../assets/svgs/common/hamburger.svg";
import { useUserStore } from "../../../store/user.store";

export const CampaignViewBrandScreen: FunctionComponent = () => {
  const { user } = useUserStore();

  const menuList = [
    {
      name: "Metrics",
      link: "campaign-metrics",
    },
    {
      name: "Live",
      link: "live-posts",
    },
    {
      name: "Drafts",
      link: "draft-submissions",
    }
  ];

  if (!user?.visibleToBrandEnabled) {
    menuList.splice(2, 0, {
      name: "Posts",
      link: "post-submissions",
    });
  }

  const [campaignData, setCampaignData] = useState<ICampaign>();
  const { fetching, execute: getCampaignByIdHttp } =
    useGetCampaignByIdEphemeralHttp();
  const [searchQuery] = useSearchParams();
  const { searchOpen, setSearchOpen } = useSearchOpenStateStore();
  const [search, setSearch] = useState(searchQuery.get("query") || "");
  const navigate = useNavigate();
  const { data, setData } = useHistoryPathStore();

  const { campaignId } = useParams();
  const { toast } = useToast();

  async function getCampaignById() {
    if (!campaignId) {
      return;
    }
    const response = await getCampaignByIdHttp({
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setCampaignData(response.data.campaign);
  }
  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
    if (e.target.value.trim().length) {
      navigate(
        `/ephemeral/brand-dash/${campaignId}/search?query=${e.target.value}`,
      );
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getCampaignById();
    setSearchOpen(false);
    setSearchOpen(window.location.pathname.includes("search"));
    setData(`/ephemeral/brand-dash/${campaignId}/live-posts`);
  }, [campaignId]);

  return (
    <div className="flex flex-col bg-background w-full min-w-0 max-w-[100vw] md:max-w-[calc(100vw-256px)]">
      <div className="flex justify-between items-center md:hidden bg-black py-5 p-4 md:p-8 text-primarybuttonbg">
        <MemedLogo />

        <div className="md:hidden">
          <Sheet>
            <SheetTrigger>
              <HamburgerIcon />
            </SheetTrigger>
            <SheetContent
              className={
                "flex flex-col justify-between border-border border-l w-64 p-0 h-full font-bold text-primary overflow-y-scroll "
              }
            >
              <DashboardSidebar alwaysShowLogout />
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="flex lg:flex-row flex-col justify-between lg:items-center gap-5 p-4 md:p-8 pb-0 md:pb-0 w-full min-w-0">
        <div className="flex flex-col min-w-0 font-semibold md:text-xl">
          <span className="text-secondary text-xs">Campaign</span>
          {fetching ? <Spinner width={20} /> : campaignData?.name}
        </div>

        <div className="flex justify-between items-center border border-border rounded-lg w-full sm:w-fit min-w-0 overflow-hidden">
          {searchOpen ? (
            ""
          ) : (
            <ul className="flex flex-nowrap items-center w-full justify-between min-w-0 overflow-x-auto no-scrollbar font-semibold">
              {menuList.map((menu) => (
                <NavLink
                  to={menu.link}
                  key={menu.link}
                  onClick={() => setSearchOpen(false)}
                  className={({ isActive }) =>
                    `px-4 lg:px-6 h-14 text-sm md:text-base flex flex-1 items-center justify-center shrink-0 whitespace-nowrap border border-transparent text-secondary rounded-lg ${isActive ? "border-white text-white" : ""}`
                  }
                >
                  {menu.name}
                </NavLink>
              ))}
            </ul>
          )}

          <ul
            className={`flex items-center shrink-0 ${searchOpen ? "w-full sm:w-96" : "bg-tertiarybuttonbg rounded-lg"} h-14 `}
          >
            <div className="relative flex items-center bg-bginput px-4 lg:px-5 py-4 rounded-10 w-full h-full">
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
              <div
                className="cursor-pointer"
                onClick={() => {
                  setSearchOpen(true);
                  setData(window.location.pathname);
                }}
              >
                <SearchIcon />
              </div>

              {searchOpen && (
                <Input
                  value={search}
                  onChange={(e) => {
                    handleSearch(e);
                  }}
                  className="bg-transparent pr-8 lg:pr-10 border-none w-full h-full font-semibold text-base placeholder:text-xs sm:placeholder:text-sm placeholder:text-secondary"
                  placeholder="Search by name, instagram ID, and number"
                />
              )}

              {searchOpen && (
                <XIcon
                  onClick={() => {
                    setSearch("");
                    setSearchOpen(false);
                    navigate(data);
                    // setData([]);
                    document.getElementById("getSocials")?.click();
                  }}
                  className="right-4 lg:right-5 absolute cursor-pointer"
                />
              )}
            </div>
          </ul>
        </div>
      </div>
      <div className="p-4 md:p-8 pt-0 md:pt-0 min-w-0">
        <Outlet />
      </div>
    </div>
  );
};
